import { IPlayerLocalState, IPlayerPlaylistResponse } from "./types";

/**
 * Persistent state for the Web Player.
 *
 * - Small fields (device_code, token, version, timestamps) live in
 *   localStorage so they're available synchronously at boot.
 * - The full cached playlist JSON lives in IndexedDB because it can be
 *   large and localStorage has a tight quota (~5MB) on TV browsers.
 */

const LS_KEY = (deviceCode: string) => `vmd_player_state__${deviceCode}`;
/** Pointer written once a bare /player browser has been paired: the
 *  device_code whose per-device state (incl. token) holds the credential. */
const LS_PAIRED_CODE = "vmd_player_paired_device_code";
/** A pending (not-yet-claimed) pairing session, so a reload during
 *  pairing resumes the SAME on-screen code instead of generating a new
 *  one every refresh. Cleared once activated or expired. */
const LS_PENDING_SESSION = "vmd_player_pending_pairing";
const IDB_NAME = "vmd_player";
const IDB_STORE_PLAYLIST = "playlist_cache";
const IDB_STORE_MEDIA = "media_blobs";
const IDB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE_PLAYLIST)) {
        db.createObjectStore(IDB_STORE_PLAYLIST);
      }
      if (!db.objectStoreNames.contains(IDB_STORE_MEDIA)) {
        db.createObjectStore(IDB_STORE_MEDIA);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet<T>(store: string, key: string): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(store, "readonly");
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbPut(store: string, key: string, value: unknown): Promise<boolean> {
  try {
    const db = await openDb();
    return await new Promise<boolean>((resolve) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).put(value as any, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}

async function idbDelete(store: string, key: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch { /* swallow */ }
}

async function idbKeys(store: string): Promise<string[]> {
  try {
    const db = await openDb();
    return await new Promise<string[]>((resolve) => {
      const tx = db.transaction(store, "readonly");
      const req = tx.objectStore(store).getAllKeys();
      req.onsuccess = () => resolve((req.result || []) as string[]);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

/* ─── Player state (small / fast / synchronous) ─────────────────────── */

export function loadState(deviceCode: string): IPlayerLocalState {
  try {
    const raw = localStorage.getItem(LS_KEY(deviceCode));
    if (raw) {
      const parsed = JSON.parse(raw) as IPlayerLocalState;
      // Defend against schema drift.
      if (parsed.device_code === deviceCode) return parsed;
    }
  } catch { /* fall through */ }
  return {
    device_code: deviceCode,
    token: null,
    playlist_version: 0,
    last_sync: null,
    cached_playlist: null,
    last_command: null,
  };
}

export function saveState(state: IPlayerLocalState): void {
  try {
    // Keep cached_playlist OUT of localStorage; it lives in IDB.
    const { cached_playlist, ...lite } = state;
    localStorage.setItem(LS_KEY(state.device_code), JSON.stringify({
      ...lite,
      cached_playlist: null,
    }));
  } catch { /* quota — best-effort only */ }
}

/* ─── Paired-device pointer + pending pairing session ───────────────── */

export function loadPairedDeviceCode(): string | null {
  try { return localStorage.getItem(LS_PAIRED_CODE) || null; }
  catch { return null; }
}

export function savePairedDeviceCode(deviceCode: string): void {
  try { localStorage.setItem(LS_PAIRED_CODE, deviceCode); } catch { /* quota */ }
}

export function clearPairedDeviceCode(): void {
  try { localStorage.removeItem(LS_PAIRED_CODE); } catch { /* best effort */ }
}

export interface PendingPairingSession {
  pairing_code: string;
  session_token: string;
  /** ISO expiry — a resumed session past this is discarded for a fresh one. */
  expires_at: string;
}

export function loadPendingPairing(): PendingPairingSession | null {
  try {
    const raw = localStorage.getItem(LS_PENDING_SESSION);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPairingSession;
    if (!parsed.session_token || !parsed.pairing_code) return null;
    // Expired sessions are useless — drop them so the caller starts fresh.
    if (parsed.expires_at && new Date(parsed.expires_at).getTime() <= Date.now()) {
      clearPendingPairing();
      return null;
    }
    return parsed;
  } catch { return null; }
}

export function savePendingPairing(session: PendingPairingSession): void {
  try { localStorage.setItem(LS_PENDING_SESSION, JSON.stringify(session)); }
  catch { /* quota — best effort */ }
}

export function clearPendingPairing(): void {
  try { localStorage.removeItem(LS_PENDING_SESSION); } catch { /* best effort */ }
}

/* ─── Cached playlist (large JSON, async) ────────────────────────────── */

export async function loadCachedPlaylist(deviceCode: string): Promise<IPlayerPlaylistResponse | null> {
  return idbGet<IPlayerPlaylistResponse>(IDB_STORE_PLAYLIST, deviceCode);
}

export async function saveCachedPlaylist(deviceCode: string, payload: IPlayerPlaylistResponse): Promise<void> {
  await idbPut(IDB_STORE_PLAYLIST, deviceCode, payload);
}

/* ─── Media blob cache (keyed by checksum) ───────────────────────────── */

export async function getMediaBlob(checksum: string): Promise<Blob | null> {
  return idbGet<Blob>(IDB_STORE_MEDIA, checksum);
}

export async function putMediaBlob(checksum: string, blob: Blob): Promise<void> {
  await idbPut(IDB_STORE_MEDIA, checksum, blob);
}

export async function deleteMediaBlob(checksum: string): Promise<void> {
  await idbDelete(IDB_STORE_MEDIA, checksum);
}

export async function listCachedChecksums(): Promise<string[]> {
  return idbKeys(IDB_STORE_MEDIA);
}

export async function estimateCacheSize(): Promise<{ bytes: number; count: number }> {
  try {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const est = await navigator.storage.estimate();
      const keys = await listCachedChecksums();
      return { bytes: est.usage || 0, count: keys.length };
    }
  } catch { /* not supported on older TV browsers */ }
  const keys = await listCachedChecksums();
  return { bytes: 0, count: keys.length };
}
